import React, { useState, useEffect, useMemo } from 'react';
import { ListingCard } from '../components/marketplace/ListingCard';
import { generateUsers, generateVendors, generateListings } from '../utils/mockData';
import { Search, SlidersHorizontal, Grid2X2, List, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { cn } from '../lib/utils';

export const MarketplacePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [realListings, setRealListings] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'listings'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRealListings(docs);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'listings');
    });
    return () => unsubscribe();
  }, []);

  const data = useMemo(() => {
    const users = generateUsers(10);
    const vendors = generateVendors(users);
    const mockListings = generateListings(vendors, 12);
    return { listings: [...realListings, ...mockListings] };
  }, [realListings]);

  return (
    <div className="space-y-6">
      {/* Header & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gem Marketplace</h1>
          <p className="text-gray-500 text-sm">Discover verified gems from Sri Lanka's top dealers.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Link 
            to="/add-listing"
            className="bg-primary text-white px-5 py-2.5 rounded-2xl font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm whitespace-nowrap"
          >
            <Plus size={18} />
            List Gem
          </Link>

          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search..." 
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="p-2.5 bg-white border border-gray-100 rounded-2xl text-gray-400 hover:text-primary hover:bg-primary/5 transition-all shadow-sm">
            <SlidersHorizontal size={18} />
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-4 border-y border-gray-100">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide w-full md:w-auto">
          {['All Gems', 'Sapphires', 'Rubies', 'Spinels', 'Emeralds', 'Padparadscha'].map((filter, idx) => (
            <button 
              key={filter}
              className={cn(
                "whitespace-nowrap px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-tight transition-all",
                idx === 0 
                  ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20" 
                  : "bg-white text-gray-400 hover:text-slate-900 hover:bg-gray-50 border border-gray-100"
              )}
            >
              {filter}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="flex-1 md:flex-none">
             <select className="w-full bg-white border border-gray-100 rounded-xl px-4 py-2 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-primary/10">
                <option>Newest Arrivals</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                <option>Weight: High to Low</option>
             </select>
          </div>
          <div className="hidden md:flex items-center gap-1 bg-gray-50 p-1 rounded-xl border border-gray-100">
            <button className="p-2 bg-white shadow-sm rounded-lg text-primary">
              <Grid2X2 size={16} />
            </button>
            <button className="p-2 text-gray-400 hover:text-slate-900 hover:bg-white/50 rounded-lg transition-colors">
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {data.listings.map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>

      {/* Pagination Placeholder */}
      <div className="flex justify-center pt-8">
        <button className="px-6 py-2 bg-white border border-gray-200 rounded-full text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors shadow-sm">
          Load More Listings
        </button>
      </div>
    </div>
  );
};
